import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Medal, Award, TrendingUp, Flame, Star, ArrowUpDown, ChevronUp, ChevronDown } from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { useState } from "react";

interface LeaderboardApiEntry {
  id: string;
  name: string;
  city: string;
  category: string;
  avgRating: number;
  totalReviews: number;
  tokenBalance: number;
  badgeCount: number;
  streakDays: number;
}

interface LeaderboardEntry extends LeaderboardApiEntry {
  rank: number;
  coveragePercent: number;
}

type SortKey = "rank" | "coveragePercent" | "avgRating" | "tokenBalance" | "streakDays";

function RankIcon({ rank }: { rank: number }) {
  if (rank === 1) return <Trophy className="w-5 h-5 text-amber-400" />;
  if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
  if (rank === 3) return <Medal className="w-5 h-5 text-amber-700" />;
  return <span className="text-sm font-bold text-muted-foreground w-5 text-center">{rank}</span>;
}

function SortableHeader({
  label,
  sortKey,
  currentSort,
  currentDir,
  onSort,
}: {
  label: string;
  sortKey: SortKey;
  currentSort: SortKey;
  currentDir: "asc" | "desc";
  onSort: (key: SortKey) => void;
}) {
  const isActive = currentSort === sortKey;
  return (
    <button
      className="flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
      onClick={() => onSort(sortKey)}
      data-testid={`sort-${sortKey}`}
    >
      {label}
      {isActive ? (
        currentDir === "desc" ? <ChevronDown className="w-3 h-3" /> : <ChevronUp className="w-3 h-3" />
      ) : (
        <ArrowUpDown className="w-3 h-3 opacity-40" />
      )}
    </button>
  );
}

export default function LeaderboardPage() {
  const { selectedLocationId } = useLocationStore();
  const [sortKey, setSortKey] = useState<SortKey>("rank");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const { data: entries = [], isLoading: locLoading } = useQuery<LeaderboardApiEntry[]>({
    queryKey: ["/api/leaderboard"],
  });

  const locationId =
    (selectedLocationId && entries.some((e) => e.id === selectedLocationId)
      ? selectedLocationId
      : undefined) || entries?.[0]?.id;

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  };

  const leaderboard: LeaderboardEntry[] = entries
    .map((entry) => ({
      ...entry,
      rank: 0,
      coveragePercent: Math.min(100, Math.round((entry.avgRating || 0) / 5 * 100)),
    }))
    .sort((a, b) => {
      if (b.tokenBalance !== a.tokenBalance) return b.tokenBalance - a.tokenBalance;
      return b.avgRating - a.avgRating;
    })
    .map((entry, i) => ({ ...entry, rank: i + 1 }))
    .sort((a, b) => {
      const valA = a[sortKey];
      const valB = b[sortKey];
      const dir = sortDir === "asc" ? 1 : -1;
      return ((valA as number) - (valB as number)) * dir;
    });

  const currentEntry = leaderboard.find((e) => e.id === locationId);
  const totalTokens = currentEntry?.tokenBalance ?? 0;
  const streakDays = currentEntry?.streakDays ?? 0;

  if (locLoading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-20" />
        ))}
      </div>
    );
  }

  return (
    <div className="p-6 pb-24 space-y-6 overflow-y-auto h-full" data-testid="page-leaderboard">
      <div>
        <h1 className="text-2xl font-bold tracking-tight" data-testid="text-leaderboard-title">
          Leaderboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          See how your locations rank against others on the platform
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-amber-500/10">
              <Trophy className="w-5 h-5 text-amber-500" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Your Best Rank</p>
              <p className="text-xl font-bold" data-testid="text-best-rank">
                #{currentEntry?.rank || "--"}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10">
              <TrendingUp className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Total Tokens</p>
              <p className="text-xl font-bold" data-testid="text-total-tokens">
                {totalTokens}
              </p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-5 flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-brand-orange/10">
              <Flame className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Current Streak</p>
              <p className="text-xl font-bold" data-testid="text-longest-streak">
                {streakDays} days
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Trophy className="w-4 h-4 text-amber-500" />
            Rankings
          </CardTitle>
        </CardHeader>
        <CardContent>
          {leaderboard.length === 0 ? (
            <div className="flex flex-col items-center py-12 text-center">
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                <Trophy className="w-8 h-8 text-primary" />
              </div>
              <p className="text-sm font-medium">No Locations Yet</p>
              <p className="text-xs text-muted-foreground mt-1">
                Add a location to start competing on the leaderboard
              </p>
            </div>
          ) : (
            <>
              <div className="hidden md:grid grid-cols-[3rem_1fr_5rem_4.5rem_4rem_4rem_4rem] gap-3 px-4 py-2 text-xs font-medium text-muted-foreground border-b border-border mb-2">
                <span>#</span>
                <span>Business</span>
                <SortableHeader label="Coverage" sortKey="coveragePercent" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Rating" sortKey="avgRating" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Tokens" sortKey="tokenBalance" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <SortableHeader label="Streak" sortKey="streakDays" currentSort={sortKey} currentDir={sortDir} onSort={handleSort} />
                <span>Badges</span>
              </div>
              <div className="space-y-1.5">
                {leaderboard.map((entry) => {
                  const isCurrent = entry.id === locationId;
                  return (
                    <div
                      key={entry.id}
                      className={`grid grid-cols-1 md:grid-cols-[3rem_1fr_5rem_4.5rem_4rem_4rem_4rem] gap-3 items-center p-3 rounded-xl transition-colors ${
                        isCurrent
                          ? "bg-brand-orange/10 border border-brand-orange/20"
                          : entry.rank <= 3
                          ? "bg-primary/5 border border-primary/10"
                          : "bg-card border border-border"
                      }`}
                      data-testid={`row-leaderboard-${entry.id}`}
                    >
                      <div className="flex items-center justify-center w-8">
                        <RankIcon rank={entry.rank} />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-sm truncate">{entry.name}</span>
                          {isCurrent && (
                            <Badge variant="outline" className="text-[10px] border-brand-orange text-brand-orange">You</Badge>
                          )}
                          {entry.rank <= 3 && (
                            <Badge variant="secondary" className="text-[10px]">Top {entry.rank}</Badge>
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {entry.city} · {entry.category}
                        </p>
                      </div>
                      <div className="text-sm font-semibold text-brand-blue">{entry.coveragePercent}%</div>
                      <div className="flex items-center gap-1 text-sm">
                        <Star className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        <span className="font-semibold">{entry.avgRating.toFixed(1)}</span>
                      </div>
                      <div className="text-sm font-semibold text-amber-500">{entry.tokenBalance}</div>
                      <div className="flex items-center gap-1 text-sm">
                        <Flame className="w-3.5 h-3.5 text-brand-orange" />
                        <span className="font-semibold">{entry.streakDays}d</span>
                      </div>
                      <div className="flex items-center gap-1 text-sm">
                        <Award className="w-3.5 h-3.5 text-primary" />
                        <span className="font-semibold">{entry.badgeCount}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
